import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { updateFrame } from "@/api/spaces";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui";
import { useProjectStore } from "@/stores/project-store";
import { useFramesStore } from "@/stores/frames-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { openExternalUrl } from "@/lib/open-external-url";
import { frameProjectId } from "@/lib/frame-project";
import { isPinSurface } from "@/desktop/pin/desktop-pin-surface";
import { useFrameSurface } from "../shared/use-frame-surface";
import {
  EditableHtmlIframe,
  detectEmbedSrc,
  type EditableHtmlIframeHandle,
  type HtmlFrameThemeMode,
} from "./editable-html-iframe";
import {
  getInitialThemeMode,
  getNextThemeMode,
  HTML_FRAME_THEME_LABEL_KEY,
  HTML_FRAME_THEME_METADATA_KEY,
} from "./html-frame-theme";
import { useHtmlAuthoredMotionRevision } from "./html-authored-motion";
import { htmlFrameActionAt, scrollHtmlFrameBy } from "./html-frame-bridge";
import { useHtmlPosterCapture } from "./use-html-poster-capture";
import { framePointFromClient, safeExternalHref } from "./html-frame-pointer";
import { useGlassBackedHtmlFrameSurface } from "./html-frame-surface";
import { useHtmlFrameCapabilities } from "./html-frame-capabilities";
import { HtmlFrameActionConfirm } from "./html-frame-action-confirm";
import { useRegisterFrameEditController } from "@/components/shapes/shared/frame-edit-controller";
import { useInlineFrameEmbed } from "@/components/shapes/shared/inline-frame-embed-context";
import { useFrameChromeHidden } from "@/components/shapes/shared/frame-renderer-context";
import {
  useFrameChromeConfig,
  type FrameChromeConfig,
} from "@/components/shapes/shared/frame-chrome-config";
import { createQueuedFrameContentSave } from "@/components/shapes/shared/queued-frame-content-save";
import { applyFramePatch } from "@/lib/frame-ingest";
import { FRAME_CONTENT_PADDING } from "@/components/shapes/shared/frame-content-padding";
import { translate, useT } from "@/i18n";
import { framesHtmlMessages } from "@/i18n/messages/frames-html";

export function HtmlFrame({ id, data, selected }: FrameProps) {
  const t = useT(framesHtmlMessages);
  const frameSurface = useFrameSurface(id);
  const d = data;
  const html = (d.content?.html as string) ?? "";
  const glassBackedHtmlFrameSurface = useGlassBackedHtmlFrameSurface();
  // A chat embed shows the frame as a passive preview inside its own card:
  // no inline tools, no gesture overlay, no pointer input, transparent,
  // themed like the app.
  const inlineEmbed = useInlineFrameEmbed();
  // A wall tile shows it the same passive way: the wheel scrolls the wall
  // and a click on the tile opens the full view.
  const onWallTile = useFrameChromeHidden();
  const passive = inlineEmbed || onWallTile;

  const embedSrc = detectEmbedSrc(html);

  const [interactive, setInteractive] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingBlank, setEditingBlank] = useState(false);
  const [blankDraft, setBlankDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const metadataSaveQueueRef = useRef(Promise.resolve());
  const savedThemeRef = useRef(d.metadata?.[HTML_FRAME_THEME_METADATA_KEY]);
  const contentSaveRef = useRef(
    createQueuedFrameContentSave({
      frameId: id,
      initialContent: d.content,
      errorTitle: () => translate(framesHtmlMessages, "saveEditsFailed"),
    }),
  );
  const [overlayCursor, setOverlayCursor] = useState<"auto" | "pointer">(
    "auto",
  );
  const [themeMode, setThemeMode] = useState<HtmlFrameThemeMode>(() =>
    getInitialThemeMode(d.metadata),
  );
  const frameRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<EditableHtmlIframeHandle>(null);
  const { revision: authoredMotionRevision, suppressNextChange } =
    useHtmlAuthoredMotionRevision(html, d.status === "streaming", editing);

  useEffect(() => {
    setThemeMode(getInitialThemeMode(d.metadata));
  }, [d.metadata]);

  useEffect(() => {
    if (!editing) contentSaveRef.current.reset(d.content);
  }, [d.content, editing]);

  useEffect(() => {
    if (!selected) {
      setInteractive(false);
    }
  }, [selected]);

  const finishEditing = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    await iframeRef.current?.commitCurrentDocument();
    const saved = await contentSaveRef.current.flush();
    savingRef.current = false;
    setSaving(false);
    if (saved) {
      setEditing(false);
      setEditingBlank(false);
      setInteractive(false);
    }
  }, []);

  useEffect(() => {
    if (!editing) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      const frameTarget =
        target instanceof Element
          ? target.closest<HTMLElement>("[data-canvas-frame-id]")
          : null;
      if (
        (target instanceof window.Node && frameRef.current?.contains(target)) ||
        frameTarget?.dataset.canvasFrameId === id
      ) {
        return;
      }
      void finishEditing();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [editing, finishEditing, id]);

  // The frame document has an opaque origin; the overlay asks it over the
  // message bridge what lies under the pointer instead of reading its DOM.
  const srcDocIframe = useCallback(
    () => (embedSrc ? null : contentRef.current?.querySelector("iframe")),
    [embedSrc],
  );

  // Page buttons act in the frame's own project, never one the page names.
  const actionProjectId = frameProjectId(d);
  const actionHost = useMemo(
    () =>
      actionProjectId ? { frameId: id, projectId: actionProjectId } : null,
    [actionProjectId, id],
  );
  const { runFromOverlay, confirmation } = useHtmlFrameCapabilities({
    host: actionHost,
    getIframe: srcDocIframe,
    interactive: interactive && !editing && !passive,
  });

  useHtmlPosterCapture({
    frameId: id,
    data: d,
    html,
    crossOriginEmbed: Boolean(embedSrc),
    editing,
    getIframe: srcDocIframe,
  });

  const overlayActionAt = useCallback(
    (point: { x: number; y: number }, activate: boolean) => {
      const iframe = srcDocIframe();
      if (!iframe) return Promise.resolve(null);
      const local = framePointFromClient(iframe, point);
      return htmlFrameActionAt(iframe, local.x, local.y, activate);
    },
    [srcDocIframe],
  );

  const handleOverlayWheel = useCallback(
    (event: React.WheelEvent) => {
      event.preventDefault();
      const iframe = srcDocIframe();
      if (iframe) scrollHtmlFrameBy(iframe, event.deltaX, event.deltaY);
    },
    [srcDocIframe],
  );

  const handleOverlayClick = useCallback(
    async (event: React.MouseEvent) => {
      if (!selected) return;
      if (overlayCursor === "pointer") {
        event.preventDefault();
        event.stopPropagation();
      }
      const action = await overlayActionAt(
        { x: event.clientX, y: event.clientY },
        true,
      );
      if (action?.kind === "host") runFromOverlay(action.action);
      if (action?.kind !== "link") return;
      const href = safeExternalHref(action.href);
      if (href) void openExternalUrl(href);
    },
    [overlayActionAt, overlayCursor, runFromOverlay, selected],
  );

  // One hover probe in flight at a time; the latest pointer position wins.
  const hoverProbeRef = useRef<{
    busy: boolean;
    next: { x: number; y: number } | null;
  }>({ busy: false, next: null });

  const handleOverlayMouseMove = useCallback(
    async (event: React.MouseEvent) => {
      const probe = hoverProbeRef.current;
      if (!selected) {
        probe.next = null;
        setOverlayCursor("auto");
        return;
      }
      probe.next = { x: event.clientX, y: event.clientY };
      if (probe.busy) return;
      probe.busy = true;
      while (probe.next) {
        const point = probe.next;
        probe.next = null;
        const action = await overlayActionAt(point, false);
        setOverlayCursor(action ? "pointer" : "auto");
      }
      probe.busy = false;
    },
    [overlayActionAt, selected],
  );

  const handleToggleTheme = useCallback(() => {
    const { currentProject, currentSpace } = useProjectStore.getState();
    if (!currentProject || !currentSpace) return;
    const nextThemeMode = getNextThemeMode(themeMode);
    const nextMetadata = {
      ...d.metadata,
      [HTML_FRAME_THEME_METADATA_KEY]: nextThemeMode,
    };
    setThemeMode(nextThemeMode);
    applyFramePatch(id, { metadata: nextMetadata });
    metadataSaveQueueRef.current = metadataSaveQueueRef.current.then(
      async () => {
        try {
          await updateFrame(currentProject.id, currentSpace.id, id, {
            metadata: nextMetadata,
          });
          savedThemeRef.current = nextThemeMode;
        } catch {
          const current = useFramesStore.getState().framesById[id];
          if (
            current?.metadata?.[HTML_FRAME_THEME_METADATA_KEY] === nextThemeMode
          ) {
            const rollback = { ...current.metadata };
            if (savedThemeRef.current === undefined) {
              delete rollback[HTML_FRAME_THEME_METADATA_KEY];
            } else {
              rollback[HTML_FRAME_THEME_METADATA_KEY] = savedThemeRef.current;
            }
            applyFramePatch(id, { metadata: rollback });
            setThemeMode(getInitialThemeMode(rollback));
          }
          useNotificationStore.getState().addSystemToast({
            id: `html-theme-save-error-${id}`,
            title: translate(framesHtmlMessages, "saveThemeFailed"),
            description: translate(framesHtmlMessages, "themeReverted"),
            level: "error",
          });
        }
      },
    );
  }, [d.metadata, id, themeMode]);

  const beginEditing = useCallback(() => {
    contentSaveRef.current.reset(d.content);
    setEditingBlank(!html);
    setBlankDraft(html);
    setEditing(true);
    setInteractive(true);
  }, [d.content, html]);

  const ThemeIcon =
    themeMode === "app"
      ? Icons.Monitor
      : themeMode === "light"
        ? Icons.Sun
        : Icons.Moon;
  const themeButtonLabel = t("themeButton", {
    theme: t(HTML_FRAME_THEME_LABEL_KEY[themeMode]),
  });
  const canEditInline = !embedSrc;
  const ownTools = canEditInline && !passive;
  useRegisterFrameEditController(id, {
    enabled: ownTools,
    editing,
    saving,
    beginEditing,
    saveEdits: finishEditing,
  });
  // An opened window's pill hosts Theme, followed by its Edit/Save.
  const chromeConfig = useMemo<FrameChromeConfig>(
    () => ({
      tools: ownTools
        ? [
            {
              kind: "button",
              id: "theme",
              label: themeButtonLabel,
              icon: ThemeIcon,
              onSelect: handleToggleTheme,
            },
          ]
        : [],
    }),
    [ThemeIcon, handleToggleTheme, ownTools, themeButtonLabel],
  );
  const opened = useFrameChromeConfig(chromeConfig);
  return (
    <div
      ref={frameRef}
      className={cn(
        frameSurface.className,
        "relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="html-frame"
      {...frameSurface.attributes}
    >
      {ownTools && !opened && (
        <div
          className="nodrag absolute right-2 top-3 z-20 flex gap-0.5"
          onPointerDown={(event) => event.stopPropagation()}
          data-testid="html-frame-inline-tools"
        >
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            title={themeButtonLabel}
            aria-label={themeButtonLabel}
            onClick={handleToggleTheme}
            data-testid="frame-chrome-theme"
          >
            <ThemeIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
      <div
        ref={contentRef}
        data-frame-measure-content="true"
        className={cn(
          "nodrag relative min-h-0 w-full min-w-0 flex-1 overflow-hidden",
          selected && "nowheel",
          // Passive in a chat embed or on a wall tile: clicks reach the card
          // or tile, the wheel scrolls the chat or the wall.
          passive && "pointer-events-none",
        )}
      >
        {html && !interactive && !editing && !passive && (
          <div
            className="absolute inset-0 z-10"
            style={{ cursor: overlayCursor }}
            onClick={handleOverlayClick}
            onDoubleClick={() => setInteractive(true)}
            onMouseMove={handleOverlayMouseMove}
            onMouseLeave={() => {
              hoverProbeRef.current.next = null;
              setOverlayCursor("auto");
            }}
            onWheel={handleOverlayWheel}
            data-testid="html-frame-overlay"
          />
        )}
        {editing && editingBlank ? (
          <textarea
            className="nodrag h-full w-full resize-none bg-transparent p-3 font-mono text-canvas-sm text-primary outline-none"
            value={blankDraft}
            onChange={(event) => {
              const next = event.target.value;
              setBlankDraft(next);
              contentSaveRef.current.update((content) => ({
                ...content,
                html: next,
              }));
            }}
            placeholder={t("writeHtml")}
            aria-label={t("htmlSource")}
            data-testid="html-frame-empty-editor"
          />
        ) : html ? (
          <EditableHtmlIframe
            ref={iframeRef}
            frameId={id}
            html={html}
            embedSrc={embedSrc}
            title={d.title}
            className="h-full w-full border-0"
            dataTestId="html-frame-iframe"
            themeMode={inlineEmbed ? "app" : themeMode}
            editable={editing}
            selectionBridge
            // A page's root scrollbar takes layout width in WebKit and
            // WebView2 alike; a desktop widget, smaller than most pages,
            // scrolls without one so the page fills it edge to edge.
            showScrollbars={!isPinSurface()}
            authoredMotionRevision={authoredMotionRevision}
            onHtmlCommit={(nextHtml) => {
              // An inline edit is not new authored content to replay.
              suppressNextChange();
              contentSaveRef.current.update((content) => ({
                ...content,
                html: nextHtml,
              }));
            }}
            transparentBackground={inlineEmbed || glassBackedHtmlFrameSurface}
            chromelessInsetBlockPx={0}
          />
        ) : (
          <div
            className={cn(
              "flex h-full items-center justify-center",
              FRAME_CONTENT_PADDING,
            )}
            data-frame-content="true"
          >
            <p className="text-canvas-base italic text-muted">{t("noHtml")}</p>
          </div>
        )}
      </div>
      {confirmation && <HtmlFrameActionConfirm confirmation={confirmation} />}
    </div>
  );
}
